import { useEffect, useRef, useCallback } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import type { TableConfig, TableDataResponse } from "./types";

interface UseTableWebSocketOptions {
  config: TableConfig;
  queryKey: QueryKey;
  visiblePks: (string | number)[];
}

interface CellUpdate {
  pk: string | number;
  data: Record<string, any>;
}

interface CellUpdateMessage {
  type: "cell_update";
  updates: CellUpdate[];
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export function useTableWebSocket({ config, queryKey, visiblePks }: UseTableWebSocketOptions) {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const queryKeyRef = useRef(queryKey);
  const visiblePksRef = useRef(visiblePks);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const unmountedRef = useRef(false);

  // Keep refs in sync to avoid stale closures
  queryKeyRef.current = queryKey;
  visiblePksRef.current = visiblePks;

  const hasLiveColumns = config.live_columns && config.live_columns.length > 0;

  const handleMessage = useCallback((event: MessageEvent) => {
    let msg: CellUpdateMessage;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }
    if (msg.type !== "cell_update" || !msg.updates) return;

    queryClient.setQueryData<TableDataResponse>(queryKeyRef.current as any, (old) => {
      if (!old) return old;
      const updateMap = new Map(msg.updates.map((u) => [String(u.pk), u.data]));
      const updatedResults = old.results.map((row) => {
        const data = updateMap.get(String(row.pk));
        return data ? { ...row, ...data } : row;
      });
      return { ...old, results: updatedResults };
    });
  }, [queryClient]);

  const connect = useCallback(() => {
    if (unmountedRef.current || !hasLiveColumns) return;

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws/admin/table/${config.model_label}/`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
      // Send current visible PKs
      if (visiblePksRef.current.length > 0) {
        ws.send(JSON.stringify({ type: "set_visible_pks", pks: visiblePksRef.current }));
      }
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      wsRef.current = null;
      if (unmountedRef.current) return;
      // Reconnect with exponential backoff
      const attempt = reconnectAttemptRef.current++;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [config.model_label, hasLiveColumns, handleMessage]);

  // Connect/disconnect on mount/unmount
  useEffect(() => {
    if (!hasLiveColumns) return;
    unmountedRef.current = false;
    connect();
    return () => {
      unmountedRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, hasLiveColumns]);

  // Send updated visible PKs when they change
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "set_visible_pks", pks: visiblePks }));
  }, [visiblePks]);
}
